import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { ArrowLeft, Monitor, Package, Clock, UserCheck, UserX, RefreshCw, X, Check, AlertTriangle } from 'lucide-react'

interface UserData { id:string; username:string; displayName?:string; email?:string; employeeId?:string; department?:string; position?:string; role:string; isActive:boolean; company?:{name:string} }
interface Assignment { id:string; assignedAt:string; returnedAt?:string; notes?:string; asset:{id:string;assetCode?:string;name:string;type:string;status:string} }
interface License { id:string; name:string; version?:string; licenseKey?:string; expiryDate?:string }
interface AssetOption { id:string; assetCode?:string; name:string; status:string }

const STATUS_TH: Record<string,string> = { IN_USE:'กำลังใช้', AVAILABLE:'ว่าง', MAINTENANCE:'ซ่อมบำรุง', RETIRED:'เลิกใช้', LOST:'สูญหาย' }
const STATUS_COLOR: Record<string,string> = { IN_USE:'bg-green-100 text-green-700', AVAILABLE:'bg-blue-100 text-blue-700', MAINTENANCE:'bg-yellow-100 text-yellow-700', RETIRED:'bg-gray-100 text-gray-500', LOST:'bg-red-100 text-red-700' }

function AssignModal({ userId, onClose }: { userId:string; onClose:()=>void }) {
  const qc = useQueryClient()
  const [assetId, setAssetId] = useState('')
  const [notes, setNotes] = useState('')
  const { data: assets=[] } = useQuery<AssetOption[]>({ queryKey:['assets-available'], queryFn:()=>api.get('/assets?status=AVAILABLE&limit=500').then(r=>r.data.assets) })
  const mut = useMutation({
    mutationFn: () => api.post('/assignments', { assetId, userId, notes }),
    onSuccess: () => { qc.invalidateQueries({ queryKey:['user-assignments', userId] }); onClose() },
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><UserCheck size={16} className="text-blue-600"/> มอบหมาย Asset</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-gray-600 block mb-1">เลือก Asset *</label>
            <select value={assetId} onChange={e=>setAssetId(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">-- เลือก Asset ที่ว่างอยู่ --</option>
              {assets.map(a=><option key={a.id} value={a.id}>{a.assetCode?`[${a.assetCode}] `:''}{a.name}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">หมายเหตุ</label>
            <input value={notes} onChange={e=>setNotes(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="เพิ่มเติม..."/>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button onClick={()=>mut.mutate()} disabled={!assetId||mut.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending?<RefreshCw size={14} className="animate-spin"/>:<Check size={14}/>} มอบหมาย
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function UserProfilePage({ isAdmin }: { isAdmin: boolean }) {
  const { id } = useParams<{id:string}>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showAssign, setShowAssign] = useState(false)
  const [tab, setTab] = useState<'current'|'history'|'software'>('current')

  const { data: user, isLoading } = useQuery<UserData>({ queryKey:['user-detail', id], queryFn:()=>api.get(`/users/${id}`).then(r=>r.data), enabled:!!id })
  const { data: assignments=[] } = useQuery<Assignment[]>({ queryKey:['user-assignments', id], queryFn:()=>api.get(`/assignments?userId=${id}`).then(r=>r.data), enabled:!!id })
  const { data: licenses=[]    } = useQuery<License[]>({   queryKey:['user-licenses', id],    queryFn:()=>api.get(`/software/user/${id}`).then(r=>r.data),    enabled:!!id && tab==='software' })

  const returnMut = useMutation({
    mutationFn: (assignId:string) => api.patch(`/assignments/${assignId}/return`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey:['user-assignments', id] }),
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })

  const current = assignments.filter(a=>!a.returnedAt)
  const history = assignments.filter(a=> a.returnedAt)

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400"><RefreshCw size={20} className="animate-spin mr-2"/> กำลังโหลด...</div>
  if (!user) return <div className="p-8 text-center text-gray-400">ไม่พบผู้ใช้</div>

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={()=>navigate('/users')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-5">
        <ArrowLeft size={16}/> กลับรายการผู้ใช้
      </button>

      {/* User Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {(user.displayName||user.username)[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{user.displayName||user.username}</h1>
                <p className="text-sm text-gray-500">@{user.username}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.isActive?'bg-green-100 text-green-700':'bg-gray-100 text-gray-500'}`}>
                {user.isActive?'ใช้งานอยู่':'ปิดใช้งาน'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {user.email       && <div><p className="text-xs text-gray-400">Email</p><p className="text-sm text-gray-700">{user.email}</p></div>}
              {user.employeeId  && <div><p className="text-xs text-gray-400">รหัสพนักงาน</p><p className="text-sm font-mono text-gray-700">{user.employeeId}</p></div>}
              {user.department  && <div><p className="text-xs text-gray-400">แผนก</p><p className="text-sm text-gray-700">{user.department}</p></div>}
              {user.position    && <div><p className="text-xs text-gray-400">ตำแหน่ง</p><p className="text-sm text-gray-700">{user.position}</p></div>}
              {user.company     && <div><p className="text-xs text-gray-400">บริษัท</p><p className="text-sm text-gray-700">{user.company.name}</p></div>}
              <div><p className="text-xs text-gray-400">สิทธิ์</p><p className="text-sm text-gray-700">{user.role}</p></div>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
            <button onClick={()=>setShowAssign(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
              <UserCheck size={14}/> มอบหมาย Asset
            </button>
            {!user.isActive && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-xl text-xs">
                <AlertTriangle size={13}/> User นี้ปิดใช้งานแล้ว — ตรวจสอบ Asset ที่ค้างอยู่ {current.length} รายการ
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{current.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Asset ที่ถืออยู่</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{history.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">ประวัติที่คืนแล้ว</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{licenses.length||'—'}</p>
          <p className="text-xs text-gray-500 mt-0.5">Software License</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {([['current','Asset ที่ถืออยู่'],['history','ประวัติ'],['software','Software']] as [string,string][]).map(([key,label])=>(
          <button key={key} onClick={()=>setTab(key as any)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${tab===key?'bg-gray-900 text-white':'text-gray-500 hover:bg-gray-100'}`}>
            {label}{key==='current'&&current.length>0&&<span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">{current.length}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        {tab==='current' && (
          <>
            {current.length===0 && <div className="text-center text-gray-400 py-10 text-sm">ไม่มี Asset ที่ถืออยู่</div>}
            <div className="space-y-2">
              {current.map(a=>(
                <div key={a.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><Monitor size={16} className="text-blue-600"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {a.asset.assetCode&&<span className="text-xs font-mono text-blue-600 font-semibold">{a.asset.assetCode}</span>}
                      <span className="text-sm font-medium text-gray-900 truncate">{a.asset.name}</span>
                    </div>
                    <p className="text-xs text-gray-400">มอบหมายตั้งแต่ {new Date(a.assignedAt).toLocaleDateString('th-TH')}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[a.asset.status]||''}`}>{STATUS_TH[a.asset.status]||a.asset.status}</span>
                  <a href={`/assets/${a.asset.id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex-shrink-0">ดู</a>
                  {isAdmin && (
                    <button onClick={()=>{if(confirm('ยืนยันการคืน Asset?'))returnMut.mutate(a.id)}}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex-shrink-0">
                      <UserX size={12}/> คืน
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {tab==='history' && (
          <>
            {history.length===0 && <div className="text-center text-gray-400 py-10 text-sm">ไม่มีประวัติ</div>}
            <div className="space-y-2">
              {history.map(a=>(
                <div key={a.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl opacity-70">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><Clock size={14} className="text-gray-400"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {a.asset.assetCode&&<span className="text-xs font-mono text-gray-500">{a.asset.assetCode}</span>}
                      <span className="text-sm text-gray-700 truncate">{a.asset.name}</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(a.assignedAt).toLocaleDateString('th-TH')} → {a.returnedAt&&new Date(a.returnedAt).toLocaleDateString('th-TH')}
                    </p>
                  </div>
                  <a href={`/assets/${a.asset.id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline flex-shrink-0">ดู</a>
                </div>
              ))}
            </div>
          </>
        )}

        {tab==='software' && (
          <>
            {licenses.length===0 && <div className="text-center text-gray-400 py-10 text-sm">ไม่มี Software License ที่ผูกกับผู้ใช้นี้</div>}
            <div className="space-y-2">
              {licenses.map(l=>(
                <div key={l.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0"><Package size={15} className="text-purple-600"/></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{l.name}{l.version&&<span className="text-gray-400 font-normal ml-1">v{l.version}</span>}</p>
                    {l.licenseKey&&<p className="text-xs font-mono text-gray-400">{l.licenseKey.slice(0,20)}...</p>}
                  </div>
                  {l.expiryDate&&<span className="text-xs text-gray-500 flex-shrink-0">{new Date(l.expiryDate).toLocaleDateString('th-TH')}</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showAssign && id && <AssignModal userId={id} onClose={()=>setShowAssign(false)}/>}
    </div>
  )
}
